import { useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { Tabs } from "@/components/ui/Tabs/Tabs";
import { AppShell } from "@/components/app/AppShell";
import { useAuth } from "@/lib/AuthContext";
import {
  useBrand,
  useAnalyzeLearning,
  useBrainVersions,
  useDecidePattern,
  useLearning,
  type BrandVersionRecord,
  type PatternRecord,
  type PatternStatus,
  type PatternSummary,
} from "@/lib/queries";
import styles from "./Learning.module.css";

/**
 * Learning — the engagement → brain learning loop surface.
 *
 * STATUS: PARTLY LIVE. The **Patterns** and **Brain diff** tabs are wired to
 * the feedback-driven learning engine (PRs F1–F2): proposed brand-brain edits
 * are derived from durable reviewer 👍/👎 feedback, and accepting one edits
 * `brand.json` and records a version snapshot. The **Posts that taught the
 * brain** and **Forecast** tabs — plus the engagement metrics in the hero —
 * depend on the post-publish metrics engine (C2/E3 in
 * docs/LEARNING_ENGINE_PLAN.md), which is NOT built yet; they stay as honest,
 * data-free scaffolds with no fabricated cards, posts, or numbers.
 *
 * Endpoints (infra/lambdas/api/learning.ts):
 *   GET  .../learning            → { patterns, summary }
 *   GET  .../learning/versions   → { versions }   (Brain-diff history)
 *   POST .../learning/analyze    → re-derive patterns from recent feedback
 *   POST .../learning/{id}/{accept|snooze|reject}
 *
 * Design source of truth: docs/design-mockups/learning.jsx + DESIGN_SPEC
 * §4.30–4.33.
 */

type TabKey = "patterns" | "posts" | "forecast" | "diff";

const TAB_ITEMS = [
  { key: "patterns", label: "Patterns learned" },
  { key: "posts", label: "Posts that taught the brain" },
  { key: "forecast", label: "Forecast" },
  { key: "diff", label: "Brain diff" },
];

export function Learning() {
  const { ws, brand } = useParams<{ ws: string; brand: string }>();
  const auth = useAuth();
  const brandQuery = useBrand(ws, brand);
  const learning = useLearning(ws, brand);
  const analyze = useAnalyzeLearning(ws, brand);
  const [tab, setTab] = useState<TabKey>("patterns");

  if (auth.status === "anonymous") {
    return <Navigate to={`/signin?next=/w/${ws}/b/${brand}/learning`} replace />;
  }

  const b = brandQuery.data?.brand;
  const summary = learning.data?.summary;

  return (
    <AppShell currentWorkspaceId={ws}>
      <div className={styles.page}>
        <Btn
          href={`/w/${ws}/b/${brand}`}
          variant="ghost"
          size="sm"
          iconLeft={<Icon name="arrow-left" size={13} />}
        >
          Back to brand
        </Btn>

        <header className={styles.head}>
          <h1 className={styles.title}>
            <Icon name="sparkles" size={20} /> Learning{b ? ` · ${b.name}` : ""}
          </h1>
          <div className={styles.headActions}>
            {/* Re-derive proposed edits from the recent feedback window. */}
            <Btn
              variant="secondary"
              loading={analyze.isPending}
              disabled={analyze.isPending}
              onClick={() => analyze.mutate()}
              iconLeft={<Icon name="refresh" size={14} />}
            >
              Re-analyze
            </Btn>
            {/* Jumps to the proposed-edits review list. */}
            <Btn
              variant="primary"
              onClick={() => setTab("patterns")}
              iconLeft={<Icon name="check" size={14} />}
            >
              Review proposed edits
            </Btn>
          </div>
        </header>

        {analyze.isError && (
          <div className={styles.bannerError} role="alert">
            <Icon name="alert" size={15} /> Couldn’t re-analyze. {analyze.error.message}
          </div>
        )}
        {analyze.isSuccess && (
          <div className={styles.bannerOk} role="status">
            <Icon name="check-circle" size={15} /> Analysis complete —{" "}
            {analyze.data.summary.proposed} proposed edit
            {analyze.data.summary.proposed === 1 ? "" : "s"} from recent feedback.
          </div>
        )}

        <PartialNotice />

        <Hero summary={summary} />

        <div className={styles.tabsBar}>
          <Tabs items={TAB_ITEMS} value={tab} onChange={(k) => setTab(k as TabKey)} />
        </div>

        {tab === "patterns" && <PatternsTab ws={ws} brand={brand} />}
        {tab === "posts" && <PostsTab />}
        {tab === "forecast" && <ForecastTab />}
        {tab === "diff" && <DiffTab ws={ws} brand={brand} />}
      </div>
    </AppShell>
  );
}

/** Honest status banner: what's live, what's waiting on the metrics engine. */
function PartialNotice() {
  return (
    <div className={styles.scaffold} role="note">
      <Icon name="info" size={15} />
      <div>
        <strong>Partly live.</strong> The <strong>Patterns</strong> and{" "}
        <strong>Brain diff</strong> tabs are wired to the feedback-driven
        learning engine — proposed brain edits are derived from your reviewers’
        👍/👎 feedback, and accepting one edits the brand brain and records a
        version. The <strong>Posts that taught the brain</strong> and{" "}
        <strong>Forecast</strong> tabs (and the engagement metrics below) await
        the post-publish metrics engine, which isn’t built yet — so they show no
        data rather than fabricated numbers. Build status lives in{" "}
        <code>docs/FEATURE_CATALOG.md</code> (“Learning — brain learning loop”).
      </div>
    </div>
  );
}

/**
 * Hero stat strip. "Proposed edits" / "Accepted" are real counts from the
 * learning summary; "Posts analyzed" / "Reach gained" depend on the unbuilt
 * post-publish metrics engine, so they render "—" (never a fabricated number).
 */
function Hero({ summary }: { summary: PatternSummary | undefined }) {
  const stats: { label: string; hint: string; value: string }[] = [
    {
      label: "Proposed edits",
      hint: "patterns awaiting your call",
      value: summary ? String(summary.proposed) : "—",
    },
    {
      label: "Accepted",
      hint: "patterns live in the brand brain",
      value: summary ? String(summary.accepted) : "—",
    },
    {
      label: "Posts analyzed",
      hint: "arrives with the post-publish metrics engine",
      value: "—",
    },
    {
      label: "Reach gained",
      hint: "arrives with the post-publish metrics engine",
      value: "—",
    },
  ];
  return (
    <section className={styles.hero}>
      <p className={styles.heroLede}>
        The engagement → brain loop. Today the engine reads durable reviewer
        feedback and proposes edits to the brand brain for your approval; once
        publishing + metrics land, it will also learn from each post’s real
        engagement.
      </p>
      <div className={styles.statRow}>
        {stats.map((s) => (
          <div key={s.label} className={styles.stat}>
            <span className={styles.statValue}>{s.value}</span>
            <span className={styles.statLabel}>{s.label}</span>
            <span className={styles.statHint}>{s.hint}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Patterns tab ───────────────────────────────────────────────────────

/** Status groups in display order. Any other status falls through to "Other". */
const STATUS_GROUPS: { status: PatternStatus; label: string }[] = [
  { status: "proposed", label: "Proposed edits" },
  { status: "informational", label: "Observations" },
  { status: "snoozed", label: "Snoozed" },
  { status: "accepted", label: "Accepted" },
  { status: "rejected", label: "Rejected" },
  { status: "regressing", label: "Regressing" },
  { status: "recommend-revert", label: "Recommend revert" },
];

function PatternsTab({ ws, brand }: { ws: string | undefined; brand: string | undefined }) {
  const learning = useLearning(ws, brand);
  const decide = useDecidePattern(ws, brand);

  const grouped = useMemo(() => {
    const patterns = learning.data?.patterns ?? [];
    return STATUS_GROUPS.map((g) => ({
      ...g,
      items: patterns.filter((p) => p.status === g.status),
    })).filter((g) => g.items.length > 0);
  }, [learning.data]);

  if (learning.isLoading) {
    return <div className={styles.muted}>Loading patterns…</div>;
  }
  if (learning.isError) {
    return (
      <div className={styles.muted}>
        Couldn’t load patterns. {learning.error.message}
      </div>
    );
  }
  if ((learning.data?.patterns ?? []).length === 0) {
    return (
      <div className={styles.emptyPane}>
        <Icon name="sparkles" size={22} />
        <p>No patterns yet.</p>
        <p className={styles.emptyHint}>
          Once your team leaves enough 👍/👎 feedback on generated captions and
          images, the engine derives proposed brand-brain edits here — each with
          its confidence, the feedback that taught it, and the exact edit it
          proposes. Use <strong>Re-analyze</strong> above to derive them from the
          current feedback window.
        </p>
      </div>
    );
  }

  const pendingId = decide.isPending ? decide.variables?.patternId : undefined;
  const errorFor =
    decide.isError && decide.variables ? decide.variables.patternId : undefined;

  return (
    <div className={styles.groups}>
      {grouped.map((g) => (
        <section key={g.status} className={styles.group}>
          <h3 className={styles.groupTitle}>
            {g.label} <span className={styles.groupCount}>{g.items.length}</span>
          </h3>
          <div className={styles.cardList}>
            {g.items.map((p) => (
              <PatternCard
                key={p.patternId}
                p={p}
                onDecide={(op, reason) =>
                  decide.mutate({ patternId: p.patternId, op, ...(reason ? { reason } : {}) })
                }
                pending={pendingId === p.patternId}
                error={errorFor === p.patternId ? decide.error?.message : undefined}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function fmtVal(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
    return String(v);
  }
  return JSON.stringify(v);
}

function PatternCard({
  p,
  onDecide,
  pending,
  error,
}: {
  p: PatternRecord;
  onDecide: (op: "accept" | "snooze" | "reject", reason?: string) => void;
  pending: boolean;
  error: string | undefined;
}) {
  const [mode, setMode] = useState<null | "snooze" | "reject">(null);
  const [reason, setReason] = useState("");

  // Only proposed / informational patterns are still actionable; the rest
  // already carry a decision (matches the API's condition guard).
  const actionable = p.status === "proposed" || p.status === "informational";
  const canAccept = p.status === "proposed" && !!p.proposedEdit;
  const confidencePct = Math.round((p.confidence ?? 0) * 100);

  const submitReasoned = (op: "snooze" | "reject") => {
    onDecide(op, reason.trim() || undefined);
  };

  return (
    <article className={styles.card}>
      <div className={styles.cardHead}>
        <p className={styles.cardHook}>{p.hook}</p>
        <span className={`${styles.domainTag} ${p.domain === "ads" ? styles.domainAds : ""}`}>
          {p.domain}
        </span>
      </div>

      <div className={styles.confidence} title={`Confidence ${confidencePct}%`}>
        <div className={styles.confidenceTrack}>
          <div className={styles.confidenceFill} style={{ width: `${confidencePct}%` }} />
        </div>
        <span className={styles.confidenceLabel}>{confidencePct}% confidence</span>
        <span className={styles.sample}>· {p.sampleSize} signal{p.sampleSize === 1 ? "" : "s"}</span>
      </div>

      {p.appliesTo.length > 0 && (
        <div className={styles.platformRow}>
          {p.appliesTo.map((pl) => (
            <span key={pl} className={styles.platformChip}>
              {pl}
            </span>
          ))}
        </div>
      )}

      {p.proposedEdit ? (
        <div className={styles.edit}>
          <Icon name="edit" size={13} />
          <code className={styles.editTarget}>{p.proposedEdit.target}</code>
          <span className={styles.editChange}>
            {fmtVal(p.proposedEdit.from)} <Icon name="arrow-right" size={12} />{" "}
            <strong>{fmtVal(p.proposedEdit.value)}</strong>
          </span>
        </div>
      ) : (
        <p className={styles.editNone}>
          Observation only — no automatic brain edit to apply.
        </p>
      )}

      {p.evidence && <p className={styles.evidence}>{p.evidence}</p>}

      {!actionable && (p.decidedAt || p.decisionReason) && (
        <p className={styles.decision}>
          {p.status === "accepted"
            ? "Applied to the brain"
            : p.status.charAt(0).toUpperCase() + p.status.slice(1)}
          {p.decidedAt ? ` · ${new Date(p.decidedAt).toLocaleString()}` : ""}
          {p.decisionReason ? ` — “${p.decisionReason}”` : ""}
        </p>
      )}

      {error && (
        <p className={styles.cardError} role="alert">
          {error}
        </p>
      )}

      {actionable && (
        <div className={styles.cardActions}>
          {mode === null ? (
            <>
              {canAccept && (
                <Btn
                  variant="primary"
                  size="sm"
                  loading={pending}
                  disabled={pending}
                  onClick={() => onDecide("accept")}
                  iconLeft={<Icon name="check" size={13} />}
                >
                  Accept edit
                </Btn>
              )}
              <Btn
                variant="secondary"
                size="sm"
                disabled={pending}
                onClick={() => setMode("snooze")}
              >
                Snooze
              </Btn>
              <Btn
                variant="ghost"
                size="sm"
                disabled={pending}
                onClick={() => setMode("reject")}
              >
                Reject
              </Btn>
            </>
          ) : (
            <div className={styles.reasonBox}>
              <textarea
                className={styles.reasonInput}
                placeholder={`Optional reason for ${mode === "snooze" ? "snoozing" : "rejecting"}…`}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                maxLength={500}
              />
              <div className={styles.reasonActions}>
                <Btn
                  variant={mode === "reject" ? "destructive" : "primary"}
                  size="sm"
                  loading={pending}
                  disabled={pending}
                  onClick={() => submitReasoned(mode)}
                >
                  Confirm {mode}
                </Btn>
                <Btn
                  variant="ghost"
                  size="sm"
                  disabled={pending}
                  onClick={() => {
                    setMode(null);
                    setReason("");
                  }}
                >
                  Cancel
                </Btn>
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

// ─── Brain diff tab ───────────────────────────────────────────────────────

function DiffTab({ ws, brand }: { ws: string | undefined; brand: string | undefined }) {
  const versions = useBrainVersions(ws, brand);
  const [copied, setCopied] = useState(false);

  const list = versions.data?.versions ?? [];

  const copyDiff = () => {
    const text = list
      .map(
        (v) =>
          `${new Date(v.createdAt).toLocaleString()}  ${v.target}\n  ${fmtVal(
            v.before,
          )} -> ${fmtVal(v.after)}  (${v.summary})`,
      )
      .join("\n\n");
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (versions.isLoading) {
    return <div className={styles.muted}>Loading brain history…</div>;
  }
  if (versions.isError) {
    return (
      <div className={styles.muted}>
        Couldn’t load brain history. {versions.error.message}
      </div>
    );
  }
  if (list.length === 0) {
    return (
      <div className={styles.emptyPane}>
        <Icon name="copy" size={22} />
        <p>No brain edits yet.</p>
        <p className={styles.emptyHint}>
          When you accept a proposed pattern, it edits the brand brain and the
          before/after of that change is recorded here — a running history of
          exactly what the brain learned and when.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.diffBar}>
        <span className={styles.muted}>
          {list.length} brain edit{list.length === 1 ? "" : "s"}, most recent first.
        </span>
        <Btn
          variant="secondary"
          size="sm"
          onClick={copyDiff}
          iconLeft={<Icon name="copy" size={13} />}
        >
          {copied ? "Copied" : "Copy diff"}
        </Btn>
      </div>
      <div className={styles.versionList}>
        {list.map((v) => (
          <VersionRow key={v.versionId} v={v} />
        ))}
      </div>
    </div>
  );
}

function VersionRow({ v }: { v: BrandVersionRecord }) {
  return (
    <article className={styles.version}>
      <div className={styles.versionHead}>
        <code className={styles.editTarget}>{v.target}</code>
        <span className={styles.versionMeta}>
          {v.source} · {new Date(v.createdAt).toLocaleString()}
        </span>
      </div>
      <div className={styles.versionChange}>
        <span className={styles.before}>{fmtVal(v.before)}</span>
        <Icon name="arrow-right" size={13} />
        <span className={styles.after}>{fmtVal(v.after)}</span>
      </div>
    </article>
  );
}

// ─── Tabs awaiting the post-publish metrics engine (honest scaffolds) ───────

/**
 * Posts tab — the high-signal published posts that contributed to a pattern.
 * Depends on the post-metrics ingestion worker (C2), which isn't built; this
 * stays a data-free scaffold rather than fabricating posts.
 */
function PostsTab() {
  return (
    <div className={styles.emptyPane}>
      <Icon name="check-circle" size={22} />
      <p>No analyzed posts yet.</p>
      <p className={styles.emptyHint}>
        {/* SCAFFOLD: ranked post list ← GET .../learning/posts, joining
            PostMetricRecord (reach, eng rate, saves, completion, delta vs
            baseline) to the published DraftRecord, plus chips linking to the
            PatternRecord each post taught. Awaits the metrics engine (C2/E3). */}
        Published posts and their platform metrics will be listed here, each
        tagged with the pattern it helped the brain learn. Nothing appears until
        the post-publish metrics engine is wired up.
      </p>
    </div>
  );
}

/**
 * Forecast tab — predicted performance for pending generations. Depends on the
 * metrics-derived effect sizes (A2/E3), which aren't built; data-free scaffold.
 */
function ForecastTab() {
  return (
    <div className={styles.emptyPane}>
      <Icon name="sparkles" size={22} />
      <p>No forecast yet.</p>
      <p className={styles.emptyHint}>
        {/* SCAFFOLD: forecast ← GET .../learning/forecast, derived from accepted
            PatternRecord effect sizes regressed against the PostMetricRecord
            baseline. Awaits the metrics engine (C2/E3). No numbers fabricated. */}
        Once published-post metrics exist, this tab will project what the next
        drafts will lean into versus avoid, with a confidence reading and the
        reasoning for each — based on observed effect sizes, not guesses.
      </p>
    </div>
  );
}
